import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { authSelector } from '@core/services/auth/state/authentication.selectors';
import { select, Store } from '@ngrx/store';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ROUTE_ANIMATIONS_ELEMENTS } from '@shared/services/animations/route.animations';
import { combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ProtocolModel } from '../models/protocol.model';
import { ProtocolService } from '../services/protocol.service';
import { protocolById, protocolUpdate } from '../store/protocol.actions';
import { ProtocolState } from '../store/protocol.reducers';
import { protocolSelector } from './../store/protocol.selectors';
import { OptionModel } from '@features/admin/option-management/models/option.model';
import { OptionType } from '@features/admin/option-management/models/option-type.enum';
import { optionSelector } from '@features/admin/option-management/store/option.selectors';
import { SubCategoryModel } from '../../admin/option-management/models/sub-category.model';
import { CategoryModel } from '../../admin/option-management/models/category.model';

@Component({
  selector: 'app-protocol-edit',
  templateUrl: './protocol-edit.component.html',
  styleUrls: ['./protocol-edit.component.scss']
})
export class ProtocolEditComponent implements OnInit {
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
  data$!: Observable<ProtocolModel>;
  form: FormGroup = this.fb.group({});
  model = {} as ProtocolModel;
  isAdmin$ = this.store.pipe(select(authSelector.isAdmin));
  // Dropdown options
  statusOptions$!: Observable<OptionModel[]>;
  investigatorOptions$!: Observable<OptionModel[]>;
  ntrCoordinatorOptions$!: Observable<OptionModel[]>;
  researchTypeOptions$!: Observable<OptionModel[]>;
  departmentOptions$!: Observable<OptionModel[]>;
  schoolOptions$!: Observable<OptionModel[]>;
  institutionOptions$!: Observable<OptionModel[]>;
  collaboratingInstitutionOptions$!: Observable<OptionModel[]>;
  sponsorTypeOptions$!: Observable<CategoryModel[]>;
  reviewTypeOptions$!: Observable<OptionModel[]>;
  reviewCycleOptions$!: Observable<OptionModel[]>;
  specialFindingOptions$!: Observable<any[]>; // SubCategoryModel[]
  fundingAgency$!: Observable<OptionModel[]>;
  natureOfResearch$!: Observable<OptionModel[]>;
  sponsoredClinicalTrial$!: Observable<OptionModel[]>;
  irbofRecord$!: Observable<any[]>;
  cededReviewTypeId?: number;
  sponsoredResearchTypeId?: number;
  formReady = false;

  filteredSponsors: { [index: number]: SubCategoryModel[] } = {};
  allSubCategories: SubCategoryModel[] = [];

  constructor(
    private fb: FormBuilder,
    private store: Store<ProtocolState>,
    private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.data$ = this.getSelectedProtocol();
    this.store.pipe(select(optionSelector.sponsorSubCategories)).subscribe((subs: SubCategoryModel[]) => {
      this.allSubCategories = subs;
    });
  }

  save(): void {
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();

    // Filter sponsorTypeCategories to only include rows where required fields are present
    const sponsorTypeCategories = (raw.sponsorTypeCategories || []).filter((entry: any) =>
      typeof entry.categoryId === 'number' &&
      typeof entry.subCategoryId === 'number'
    );

    const updated = {
      ...raw,
      sponsorTypeCategories,
      id: this.model.id
    };

    this.form.markAsPristine();
    this.store.dispatch(protocolUpdate({ data: { protocol: updated } }));
  }

  cancel(): void {
     window.history.back();
  }

  private getSelectedProtocol(): Observable<ProtocolModel> {
    const params$ = this.route.params;
    const selected$ = this.store.pipe(
      select(protocolSelector.protocolSelected),
      tap((protocol: ProtocolModel) => {
        if (protocol?.id != null) {
          this.model = { ...protocol };
          this.initForm(protocol);
          this.initDropdowns(protocol);
        }
      })
    );

    return combineLatest([selected$, params$]).pipe(map(([selected, params]) => {
      if (selected?.id == null || selected?.id !== Number(params.id)) {
        this.store.dispatch(protocolById({ id: Number(params.id) }));
      }
      return selected;
    }));
  }

  private initForm(model: ProtocolModel): void {

    this.sponsorTypeCategoriesForm?.clear(); // clear previous form array if it exists
    const sponsorTypeArray = this.fb.array([]);
    model.sponsorTypeCategories?.forEach((entry, i) => {
      sponsorTypeArray.push(
        this.fb.group({
          categoryId: [entry.categoryId ?? null, Validators.required],
          subCategoryId: [entry.subCategoryId ?? null, Validators.required],
          sponsorProtocol: [entry.sponsorProtocol ?? '', Validators.maxLength(250)],
        })
      );
    });

    this.form = this.fb.group({
      studyTitle: [model.studyTitle || '', Validators.required],
      irbNetNumber: [model.irbNetNumber || '', Validators.maxLength(250)],
      irbNumber: [model.irbNumber || ''], //hidden field required for backend
      statusId: [model.statusId || null],
      previousIrbNumber: [model.previousIrbNumber || ''], //hidden field required for backend
      investigatorId: [model.investigatorId || null],
      coInvestigator: [model.coInvestigator || '', Validators.maxLength(250)],
      ntrIrbCoordinatorId: [model.ntrIrbCoordinatorId || null],
      crCoordinator: [model.crCoordinator || '', Validators.maxLength(250)],
      typeOfResearchId: [model.typeOfResearchId || null],
      departmentId: [model.departmentId || null],
      schoolId: [model.schoolId || null],
      institutionId: [model.institutionId || null],
      collaboratingInstitutionId: [model.collaboratingInstitutionId || null],
      sponsorTypeId: [model.sponsorTypeId || null],
      sponsor: [model.sponsor || '', Validators.maxLength(250)],
      sponsorProtocol: [model.sponsorProtocol || '', Validators.maxLength(250)],
      sponsorTypeCategories: sponsorTypeArray,
      typeOfReviewId: [model.typeOfReviewId || null],
      reviewCycleId: [model.reviewCycleId || null],
      specialFindingCategories: [model.specialFindingCategories || []],
      specialFindings: [model.specialFindings || ''],
      submissionDate: [model.submissionDate || null],
      initialReview: [model.initialReview || null],
      mostRecentReview: [model.mostRecentReview || null],
      initialApprovalDate: [model.initialApprovalDate || null],
      approvalThrough: [model.approvalThrough || null],
      sendNextReview: [model.sendNextReview || null],
      continuingReviewDate: [model.continuingReviewDate || null],
      completeDate: [model.completeDate || null],
      irbofRecordId: [model.irbofRecordId || null],
      fundingAgencyId: [model.fundingAgencyId || null],
      natureOfResearchId: [model.natureOfResearchId || null],
      sponsoredClinicalTrialId: [model.sponsoredClinicalTrialId || null]
    });

    // Populate filteredSponsors for each row (used in our select dropdowns)
    model.sponsorTypeCategories?.forEach((entry, i) => {
      this.filteredSponsors[i] = this.allSubCategories.filter(
        s => s.categoryId === entry.categoryId &&
          (s.isActive || s.id === entry.subCategoryId)
      );
    });

    // Push one row on start if empty for consistency
    if (!model.sponsorTypeCategories?.length) {
      this.addSponsorTypeCategory();
    }

    this.formReady = true;
  }

  private initDropdowns(model: ProtocolModel): void {
    const get = (type: OptionType, selectedId?: number) =>
      this.store.pipe(
        select(optionSelector.optionsByType(type, true)),
        map(options => this.appendSelectedOptionIfDisabled(selectedId ?? 0, options))
      );

    this.statusOptions$ = get(OptionType.status, model.statusId);
    this.investigatorOptions$ = get(OptionType.investigator, model.investigatorId);
    this.ntrCoordinatorOptions$ = get(OptionType.nTRIRBCoordinator, model.ntrIrbCoordinatorId);
    this.departmentOptions$ = get(OptionType.department, model.departmentId);
    this.schoolOptions$ = get(OptionType.school, model.schoolId);
    this.institutionOptions$ = get(OptionType.institution, model.institutionId);
    this.collaboratingInstitutionOptions$ = get(OptionType.collaboratingInstitution, model.collaboratingInstitutionId);

    const usedCategoryIds = new Set<number>(
      (model.sponsorTypeCategories || []).map(entry => entry.categoryId)
    );

    this.sponsorTypeOptions$ = this.store.pipe(
      select(optionSelector.categories),
      map(categories => categories
        .filter(cat => cat.type?.toLowerCase() === 'sponsor')
        .filter(cat => cat.isActive || usedCategoryIds.has(cat.id))
      )
    );

    this.fundingAgency$ = get(OptionType.fundingAgency, this.model.fundingAgencyId);
    this.natureOfResearch$ = get(OptionType.natureOfResearch, this.model.natureOfResearchId);
    this.sponsoredClinicalTrial$ = get(OptionType.sponsoredClinicalTrial, this.model.sponsoredClinicalTrialId);
    this.irbofRecord$ = get(OptionType.irbofRecord, this.model.irbofRecordId);
    this.reviewTypeOptions$ = get(OptionType.typeOfReview, model.typeOfReviewId).pipe(
      tap(options => {
        const ceded = options.find(o => o.name.toLowerCase() === 'ceded');
        this.cededReviewTypeId = ceded?.id;
        this.setupIrbofRecordValidationWatcher();
      })
    );

    this.researchTypeOptions$ = get(OptionType.typeOfResearch, this.model.typeOfResearchId).pipe(
      tap(options => {
        const typeOfResearch = options.find(o => o.name.toLowerCase() === 'sponsored clinical trial (drug/device/biologic)');
        this.sponsoredResearchTypeId = typeOfResearch?.id;
        this.setupsponsoredClinicalTrialValidationWatcher();
      })
    );
    
    
    this.reviewCycleOptions$ = get(OptionType.reviewCycle, model.reviewCycleId);

    this.specialFindingOptions$ = this.store.pipe(
      select(optionSelector.specialFindings),
      map(options => {
        const active = options.filter(o => o.isActive);
        const inactive = options.filter(o =>
          model.specialFindingCategories?.includes(o.id) && !o.isActive
        );
        return [...active, ...inactive].sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  private appendSelectedOptionIfDisabled(id: number, options: OptionModel[]): OptionModel[] {
    const selectedOption = options.find(x => x.id === id);
    const activeOptions = options.filter(x => x.isActive === true);
    if (selectedOption && !selectedOption.isActive) {
      activeOptions.push(selectedOption);
    }
    return activeOptions.sort((a, b) => a.name.localeCompare(b.name));
  }

  dateFields = [
    { label: 'Submission Date', controlName: 'submissionDate' },
    { label: 'Initial Review Date', controlName: 'initialReview' },
    { label: 'Most Recent Review Date', controlName: 'mostRecentReview' },
    { label: 'Initial Approval Date', controlName: 'initialApprovalDate' },
    { label: 'Approval Through Date', controlName: 'approvalThrough' },
    { label: 'Send Next Review Date', controlName: 'sendNextReview' },
    { label: 'Continuing Review Date', controlName: 'continuingReviewDate' },
    { label: 'Complete Date', controlName: 'completeDate' }
  ];

  private setupIrbofRecordValidationWatcher(): void {
    const typeOfReviewControl = this.form.get('typeOfReviewId');
    const irbofRecordControl = this.form.get('irbofRecordId');
  
    if (!typeOfReviewControl || !irbofRecordControl) return;
  
    typeOfReviewControl.valueChanges.subscribe((value) => {
      if (value === this.cededReviewTypeId) {
        irbofRecordControl.setValidators([Validators.required]);
      } else {
        irbofRecordControl.clearValidators();
        irbofRecordControl.setValue(null);
      }
      irbofRecordControl.updateValueAndValidity();
    });
  
    // Also handle initial load
    if (typeOfReviewControl.value === this.cededReviewTypeId) {
      irbofRecordControl.setValidators(Validators.required);
      irbofRecordControl.updateValueAndValidity();
    }
  }

  private setupsponsoredClinicalTrialValidationWatcher(): void {
    const typeOfResearchControl = this.form.get('typeOfResearchId');
    const sponsoredClinicalTrialControl = this.form.get('sponsoredClinicalTrialId');
  
    if (!typeOfResearchControl || !sponsoredClinicalTrialControl) return;
  
    typeOfResearchControl.valueChanges.subscribe((value) => {
      if (value === this.sponsoredResearchTypeId) {
        sponsoredClinicalTrialControl.setValidators([Validators.required]);
      } else {
        sponsoredClinicalTrialControl.clearValidators();
        sponsoredClinicalTrialControl.setValue(null);
      }
      sponsoredClinicalTrialControl.updateValueAndValidity();
    });
  
    // Also handle initial load
    if (typeOfResearchControl.value === this.sponsoredResearchTypeId) {
      typeOfResearchControl.setValidators([Validators.required]);
      typeOfResearchControl.updateValueAndValidity();
    }
  }

  get sponsorTypeCategoriesForm(): FormArray {
    return this.form.get('sponsorTypeCategories') as FormArray;
  }

  onSponsorTypeChange(index: number): void {
    const group = this.sponsorTypeCategoriesForm.at(index);
    const selectedTypeId = group.get('categoryId')?.value;

    // Clear the Sponsor (subCategoryId) every time category changes
    group.get('subCategoryId')?.reset();
    group.get('subCategoryId')?.markAsTouched(); // Ensure validation is re-run

    if (!selectedTypeId) {
      this.filteredSponsors[index] = [];
      return;
    }

    const currentSubCategoryId = this.sponsorTypeCategoriesForm.at(index).get('subCategoryId')?.value;
    this.filteredSponsors[index] = this.allSubCategories.filter(
      s => s.categoryId === selectedTypeId &&
        (s.isActive || s.id === currentSubCategoryId)
    );
  }

  removeSponsorTypeCategory(index: number): void {
    this.sponsorTypeCategoriesForm.removeAt(index);
    this.form.markAsDirty();
  }

  addSponsorTypeCategory(): void {
    const sponsorForm = this.fb.group({
      categoryId: [null, Validators.required],
      subCategoryId: [null, Validators.required],
      sponsorProtocol: ['', Validators.maxLength(250)],
    });

    const index = this.sponsorTypeCategoriesForm.length;

    this.sponsorTypeCategoriesForm.push(sponsorForm);
    this.filteredSponsors[index] = [];
  }

}
